import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SPORTS_VOD_SOURCES,
  type SportsVodSource,
  type SportsVodSportInfo,
} from '@/types/sportsVod';

interface Props {
  sports: SportsVodSportInfo[];
  selectedSports: string[];
  selectedSources: SportsVodSource[];
  query: string;
  onToggleSport: (id: string) => void;
  onToggleSource: (id: SportsVodSource) => void;
  onQueryChange: (q: string) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

function sportLabel(id: string): string {
  if (id === '247') return '24/7';
  return id.replace(/-/g, ' ').replace(/(^|\s)\w/g, (c) => c.toUpperCase());
}

export function SportsVodFilters({
  sports,
  selectedSports,
  selectedSources,
  query,
  onToggleSport,
  onToggleSource,
  onQueryChange,
}: Props): JSX.Element {
  const { t } = useTranslation();
  // Debounce typing — emit a trimmed value through `onQueryChange` after a
  // dwell so we don't fire one slice/api call per keystroke. The debounced
  // value reaches the parent (and via that the URL and the slice query).
  const [draft, setDraft] = useState(query);
  const debounceRef = useRef<number | null>(null);
  const lastEmittedRef = useRef(query);

  // External `query` prop drives the controlled draft on mount + when the
  // user navigates between filter URLs. Skip when the parent is just
  // echoing our last emit so we don't undo mid-typing edits.
  useEffect(() => {
    if (query !== lastEmittedRef.current) {
      setDraft(query);
      lastEmittedRef.current = query;
    }
  }, [query]);

  function handleQueryChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setDraft(value);
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const trimmed = value.trim();
      if (trimmed !== lastEmittedRef.current) {
        lastEmittedRef.current = trimmed;
        onQueryChange(trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const sportsSelectedSet = new Set(selectedSports);
  const sourcesSelectedSet = new Set(selectedSources);

  return (
    <section className="sports-vod-filters" aria-label={t('sportsVod.filters.sectionLabel')}>
      <div className="sports-vod-filters__search">
        <input
          type="search"
          placeholder={t('sportsVod.filters.searchPlaceholder')}
          value={draft}
          onChange={handleQueryChange}
          aria-label={t('sportsVod.filters.searchLabel')}
          autoComplete="off"
          enterKeyHint="search"
          data-testid="sports-vod-search"
        />
      </div>

      {sports.length > 0 && (
        <div
          className="sports-vod-filters__chips"
          role="group"
          aria-label={t('sportsVod.filters.filterBySport')}
          data-testid="sports-vod-sport-chips"
        >
          {sports.map((s) => {
            const active = sportsSelectedSet.has(s.id);
            return (
              <button
                key={s.id}
                type="button"
                className={active ? 'chip chip--active' : 'chip'}
                aria-pressed={active}
                onClick={() => onToggleSport(s.id)}
                data-testid={`sports-vod-sport-chip-${s.id}`}
              >
                {sportLabel(s.id)}
                <span className="chip__count" aria-hidden="true"> · {s.count}</span>
              </button>
            );
          })}
        </div>
      )}

      <details className="sports-vod-filters__sources">
        <summary>{t('sportsVod.filters.sources')}{selectedSources.length > 0 && ` (${selectedSources.length})`}</summary>
        <div role="group" aria-label={t('sportsVod.filters.filterBySource')} data-testid="sports-vod-source-list">
          {SPORTS_VOD_SOURCES.map((source) => {
            const active = sourcesSelectedSet.has(source);
            return (
              <label key={source} className="sports-vod-filters__source-row">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => onToggleSource(source)}
                  data-testid={`sports-vod-source-${source}`}
                />
                <span>{source}</span>
              </label>
            );
          })}
        </div>
      </details>
    </section>
  );
}
